import { cn } from '@/lib/utils';
import { type SearchCriteriaLabel, type SearchType } from '@/types/search-criteria';
import { X } from 'lucide-react';

interface SearchCriteriaChipsProps {
    labels: SearchCriteriaLabel[];
    onRemove: (label: SearchCriteriaLabel) => void;
    type?: SearchType;
    /** "Entendi assim:" right after a smart-search phrase; "Sua busca:" once the visitor changed a filter by hand. */
    title?: string | null;
    className?: string;
    size?: 'sm' | 'md';
    ariaLabel?: string;
}

/**
 * The labels of a search as removable chips (CN-200). Each chip carries the URL of the same search without it, so
 * removing one is a navigation decided by the server (same rule for boats, companies, alerts and similar blocks).
 */
export function SearchCriteriaChips({
    labels,
    onRemove,
    type = 'embarcacoes',
    title = null,
    className,
    size = 'md',
    ariaLabel = 'Filtros da busca',
}: SearchCriteriaChipsProps) {
    if (labels.length === 0) {
        return null;
    }

    const accent = type === 'empresas' ? 'border-[#0f766e]/40 bg-[#0f766e]/5 text-[#0f766e]' : 'border-blue-200 bg-blue-50 text-blue-900';

    return (
        <div className={cn('flex flex-wrap items-center gap-2', className)}>
            {title && <span className="text-sm font-semibold text-gray-900">{title}</span>}
            <ul className="flex flex-wrap gap-2" aria-label={ariaLabel}>
                {labels.map((label) => (
                    <li key={label.id}>
                        <span
                            className={cn(
                                'inline-flex max-w-full items-center rounded-full border pl-3 font-medium',
                                accent,
                                size === 'sm' ? 'text-xs' : 'text-sm',
                            )}
                        >
                            <span className="truncate py-1.5" title={label.text}>
                                {label.text}
                            </span>
                            <button
                                type="button"
                                onClick={() => onRemove(label)}
                                aria-label={`Tirar ${label.text}`}
                                className={cn(
                                    'ml-1 flex shrink-0 items-center justify-center rounded-full transition-colors hover:bg-black/10',
                                    size === 'sm' ? 'h-7 w-7' : 'h-9 w-9 sm:h-8 sm:w-8',
                                )}
                            >
                                <X aria-hidden="true" className={size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
                            </button>
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
